import type { QueryClient } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { batchLoader, isPlaceholderMetadata } from '../metadata';
import type { LinkMetadata } from '../types';
import {
  computeFavicon,
  determineShouldAttemptLiveFetch,
  fetchLiveMetadata,
  isTwitterImageUrl,
  resolvePlaceholderFlag,
  resolvesToTweetUrl,
} from '../utils';

interface FetchMetadataArguments {
  propsMetadata?: LinkMetadata;
  queryClient: QueryClient;
  url: string;
}

export async function fetchMetadataWithFallback({
  url,
  propsMetadata,
  queryClient,
}: FetchMetadataArguments): Promise<LinkMetadata | null> {
  let result: LinkMetadata | null | undefined = propsMetadata;

  if (!result) {
    result = queryClient.getQueryData<LinkMetadata>(['url-metadata', url]);
  }

  if (!result) {
    result = await batchLoader.load(url);
  }

  if (result) {
    const safeUrl = typeof result.url === 'string' ? result.url : url;
    const favicon =
      typeof result.favicon === 'string' && result.favicon.length > 0 ? result.favicon : computeFavicon(safeUrl);
    result = {
      ...result,
      favicon: favicon ?? null,
    };
  }

  const placeholder = isPlaceholderMetadata(result, url);
  const shouldAttemptLive = determineShouldAttemptLiveFetch({ url, metadata: result, placeholder });

  if (shouldAttemptLive) {
    let live = await fetchLiveMetadata(url);
    if (live) {
      const safeUrl = typeof live.url === 'string' ? live.url : url;
      const favicon =
        typeof live.favicon === 'string' && live.favicon.length > 0 ? live.favicon : computeFavicon(safeUrl);
      live = {
        ...live,
        favicon: favicon ?? null,
      };
      queryClient.setQueryData(['url-metadata', url], live);
      return live;
    }
  }

  if (result && !placeholder) {
    return result;
  }

  return result ?? null;
}

export interface UseLinkPreviewDataOptions {
  isExcluded: boolean;
  metadata?: LinkMetadata | undefined;
  url: string;
}

export interface UseLinkPreviewDataResult {
  isError: boolean;
  isLoading: boolean;
  isPlaceholder: boolean;
  metadata: LinkMetadata | null;
  resolvedImageIsTwitterImage: boolean;
  resolvedIsTweetUrl: boolean;
  resolvedIsTwitterImage: boolean;
  showPreview: boolean;
  status: 'idle' | 'loading' | 'success' | 'error';
}

export function useLinkPreviewData({
  url,
  metadata: propertiesMetadata,
  isExcluded,
}: UseLinkPreviewDataOptions): UseLinkPreviewDataResult {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['url-metadata', url],
    queryFn: async () =>
      fetchMetadataWithFallback({
        url,
        ...(propertiesMetadata ? { propsMetadata: propertiesMetadata } : {}),
        queryClient,
      }),
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 7 * 24 * 60 * 60 * 1000,
    retry: false,
    enabled: !!url && !isExcluded,
    initialData: propertiesMetadata,
  });

  const status = useMemo<'idle' | 'loading' | 'success' | 'error'>(() => {
    if (!url || isExcluded) {
      return 'idle';
    }
    if (query.status === 'pending') {
      return 'loading';
    }
    if (query.status === 'error') {
      return 'error';
    }
    return 'success';
  }, [isExcluded, query.status, url]);

  const metadata = query.data ?? null;
  const metadataUrl = typeof metadata?.url === 'string' ? metadata.url : null;
  const metadataImage = typeof metadata?.image === 'string' ? metadata.image : null;
  const metadataTitle = typeof metadata?.title === 'string' ? metadata.title : null;
  const metadataDescription = typeof metadata?.description === 'string' ? metadata.description : null;
  const isPlaceholder = resolvePlaceholderFlag(url, metadata);
  const resolvedIsTwitterImage = metadataUrl ? isTwitterImageUrl(metadataUrl) : false;
  const resolvedImageIsTwitterImage = metadataImage ? isTwitterImageUrl(metadataImage) : false;
  const resolvedIsTweetUrl = resolvesToTweetUrl(metadata);
  const showPreview = Boolean(metadata && (metadataTitle || metadataDescription || metadataImage));

  return {
    metadata,
    status,
    isLoading: query.isLoading,
    isError: query.isError,
    isPlaceholder,
    showPreview,
    resolvedIsTwitterImage,
    resolvedImageIsTwitterImage,
    resolvedIsTweetUrl,
  };
}
